import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, Sparkles, Gem, Share2, CheckCircle2, Copy, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { RarityReveal, rarityMeta } from "@/components/RarityBadge";

/**
 * 3-step publish wizard:
 *   1. Rarity reveal (regrades if missing)
 *   2. Price slider (0 to rarity.max)
 *   3. Confirm + thumbnail upload + done
 *
 * Props:
 *   scId            — id of the study to publish
 *   onClose         — called when the dialog should close (any state)
 *   onPublished     — called after a successful publish with {slug, url}
 *   getThumbnailBase64 — () => Promise<string|null> for the optional OG thumbnail upload
 */
export default function PublishWizard({ scId, study, onClose, onPublished, getThumbnailBase64 }) {
  const [step, setStep] = useState(0); // 0 rarity · 1 price · 2 confirm · 3 done
  const [rarity, setRarity] = useState(study?.rarity || null);
  const [reason, setReason] = useState(study?.rarity_reason || "");
  const [loadingGrade, setLoadingGrade] = useState(false);

  const meta = rarity ? rarityMeta(rarity) : rarityMeta("common");
  const [price, setPrice] = useState(meta.suggested);
  const [desc, setDesc] = useState("");

  const [publishing, setPublishing] = useState(false);
  const [published, setPublished] = useState(null); // { slug, url }

  // If study lacks a rarity, regrade on mount.
  useEffect(() => {
    if (rarity) return;
    setLoadingGrade(true);
    api.post(`/studies/${scId}/regrade`)
      .then((r) => {
        setRarity(r.data.rarity);
        setReason(r.data.reason);
        setPrice(r.data.suggested_gems);
      })
      .catch(() => {
        setRarity("common");
        setReason("We couldn't auto-grade this study just now — defaulted to Common.");
      })
      .finally(() => setLoadingGrade(false));
  }, [scId, rarity]);

  // Keep price in valid range whenever rarity changes.
  useEffect(() => {
    if (!rarity) return;
    setPrice((p) => Math.max(0, Math.min(p, rarityMeta(rarity).max)));
  }, [rarity]);

  const closeAndReset = () => { onClose(); };

  const doPublish = async () => {
    if (publishing) return;
    setPublishing(true);
    try {
      // Best-effort thumbnail
      let thumb = null;
      try { thumb = (await getThumbnailBase64?.()) || null; } catch {/*ignore*/}

      const { data } = await api.post("/shared-packs/publish", {
        sc_id: scId,
        description: desc.trim() || null,
        thumbnail_b64: thumb,
        price_gems: price,
      });
      const slug = data.slug;
      const backend = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      const url = `${backend.replace(/\/+$/, "")}/api/p/${slug}`;
      setPublished({ slug, url });
      setStep(3);
      onPublished?.({ slug, url });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't publish — try again.");
    } finally {
      setPublishing(false);
    }
  };

  const onCopy = () => {
    if (!published) return;
    navigator.clipboard?.writeText(published.url);
    toast.success("Link copied!");
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/70 backdrop-blur-md"
        onClick={closeAndReset}
        data-testid="publish-wizard"
      >
        <motion.div
          initial={{ opacity: 0, y: 16, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 12, scale: 0.97 }}
          transition={{ type: "spring", stiffness: 280, damping: 28 }}
          onClick={(e) => e.stopPropagation()}
          className="relative w-full max-w-[540px] bg-[#0E0E11] rounded-3xl border border-white/[0.08] overflow-hidden"
          style={{ boxShadow: `0 30px 80px -20px ${meta.glow}, 0 0 0 1px ${meta.color}22` }}
        >
          {/* Glow background */}
          <div
            aria-hidden
            className="absolute inset-0 pointer-events-none opacity-40"
            style={{ background: `radial-gradient(600px 300px at 50% -20%, ${meta.color}22, transparent 70%)` }}
          />

          {/* Header */}
          <header className="relative flex items-center justify-between px-6 py-4 border-b border-white/[0.06]">
            <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#8E8B99]">PUBLISH PACK</div>
            <div className="flex items-center gap-3 text-[11px] text-[#8E8B99] font-bold">
              {[0, 1, 2].map((i) => (
                <span
                  key={i}
                  className="w-6 h-1 rounded-full transition-all"
                  style={{
                    background: i <= step ? meta.color : "rgba(255,255,255,0.08)",
                    boxShadow: i <= step ? `0 0 10px ${meta.glow}` : "none",
                  }}
                />
              ))}
            </div>
            <button
              onClick={closeAndReset}
              data-testid="publish-close-btn"
              className="text-[#8E8B99] hover:text-white p-1 -mr-1 rounded-full"
            ><X size={16} /></button>
          </header>

          {/* Body */}
          <div className="relative p-7 md:p-8">
            <AnimatePresence mode="wait" initial={false}>
              {step === 0 && (
                <motion.div key="step0" initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -10 }}>
                  {loadingGrade ? (
                    <div className="text-center py-14">
                      <Loader2 size={28} className="mx-auto animate-spin text-[#B8A4FF] mb-4" />
                      <p className="text-[13.5px] text-[#B0AEBC]">Grading your pack…</p>
                      <p className="text-[12px] text-[#71717A] mt-1">Studivault AI is judging depth + breadth.</p>
                    </div>
                  ) : (
                    <RarityReveal rarity={rarity} reason={reason} />
                  )}
                </motion.div>
              )}

              {step === 1 && (
                <motion.div key="step1" initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -10 }}>
                  <div className="eyebrow mb-2">PRICE THE PACK</div>
                  <h3 className="text-[20px] font-semibold mb-1.5">Set your import price.</h3>
                  <p className="text-[12.5px] text-[#8E8B99] leading-relaxed mb-7">
                    Importers pay this in gems — 100% goes straight to your balance. Suggested for {rarityMeta(rarity).label}: <span className="text-white font-semibold">{rarityMeta(rarity).suggested} gems</span>.
                  </p>

                  {/* Price slider */}
                  <PriceSlider
                    value={price}
                    onChange={setPrice}
                    max={rarityMeta(rarity).max}
                    color={meta.color}
                  />

                  {/* Free / paid hint */}
                  <div className="mt-6 grid grid-cols-2 gap-3">
                    <Hint title={price === 0 ? "Free pack" : `${price} gem${price > 1 ? "s" : ""} per import`}
                      body={price === 0
                        ? "Best for visibility — free packs get more imports + drive your reputation."
                        : "Earn gems each time someone imports. Boost reach later with a 24h pack boost."} />
                    <Hint title="You can change it later"
                      body="Edit the price from My Packs at any time. Existing imports remain valid." />
                  </div>

                  <label className="block mt-6">
                    <div className="eyebrow text-[10.5px] mb-2">DESCRIPTION (OPTIONAL)</div>
                    <textarea
                      value={desc}
                      onChange={(e) => setDesc(e.target.value)}
                      rows={2}
                      maxLength={500}
                      placeholder="Why should someone import this? Best topic, prep level, anything else."
                      className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-[13px] focus:outline-none focus:border-[#7C5CFF] resize-y"
                      data-testid="publish-description"
                    />
                  </label>
                </motion.div>
              )}

              {step === 2 && (
                <motion.div key="step2" initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -10 }}>
                  <div className="eyebrow mb-2">REVIEW</div>
                  <h3 className="text-[20px] font-semibold mb-1.5">Ready to publish?</h3>
                  <p className="text-[12.5px] text-[#8E8B99] mb-6 leading-relaxed">
                    Your pack will be visible on <span className="text-white font-semibold">/explore</span>{" "}
                    and shareable via a link with a generated thumbnail preview.
                  </p>

                  <div className="space-y-2.5 mb-6">
                    <Row k="Title" v={study?.title || "Untitled study"} />
                    <Row k="Rarity" v={<span className="font-bold" style={{ color: meta.color }}>{meta.label}</span>} />
                    <Row k="Price" v={
                      price === 0
                        ? <span className="text-white font-bold">FREE</span>
                        : <span className="text-white font-bold tabular-nums inline-flex items-center gap-1">
                            <Gem size={11} style={{ color: "#B8A4FF" }} /> {price}
                          </span>
                    } />
                    {desc.trim() && <Row k="Description" v={desc} />}
                  </div>

                  <div className="text-[11.5px] text-[#71717A] leading-relaxed">
                    By publishing you grant Studivault a limited licence to display this pack on the public marketplace.
                    Imported packs remain available to importers even if you later unpublish.
                  </div>
                </motion.div>
              )}

              {step === 3 && published && (
                <motion.div key="step3"
                  initial={{ opacity: 0, scale: 0.94 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4 }}
                  className="text-center py-3"
                  data-testid="publish-success"
                >
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1, rotate: 360 }}
                    transition={{ type: "spring", stiffness: 260, damping: 20 }}
                    className="mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4"
                    style={{ background: "linear-gradient(135deg,#7C5CFF,#5AC8FA)", boxShadow: "0 0 40px -8px rgba(124,92,255,0.7)" }}
                  >
                    <CheckCircle2 size={28} className="text-white" />
                  </motion.div>
                  <h3 className="text-[22px] font-bold mb-1">Live on Explore!</h3>
                  <p className="text-[13px] text-[#B0AEBC] mb-5">Share the link — it includes a thumbnail preview on Twitter/Discord/iMessage.</p>
                  <div className="bg-black/40 border border-white/10 rounded-xl px-3 py-2 flex items-center gap-2 mb-5">
                    <code className="text-[11.5px] text-[#B8A4FF] truncate flex-1 text-left">{published.url}</code>
                    <button onClick={onCopy} data-testid="publish-copy-link" className="text-[#B0AEBC] hover:text-white p-1 rounded">
                      <Copy size={13} />
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Footer */}
          <footer className="relative px-6 py-4 border-t border-white/[0.06] flex items-center justify-between gap-3">
            {step < 3 && (
              <>
                <button
                  onClick={() => step === 0 ? closeAndReset() : setStep((s) => s - 1)}
                  disabled={publishing || loadingGrade}
                  className="text-[12.5px] font-semibold text-[#B0AEBC] hover:text-white disabled:opacity-40 flex items-center gap-1"
                  data-testid="publish-back-btn"
                >
                  <ChevronLeft size={13} /> {step === 0 ? "Cancel" : "Back"}
                </button>
                {step < 2 ? (
                  <button
                    onClick={() => setStep((s) => s + 1)}
                    disabled={loadingGrade || !rarity}
                    data-testid="publish-next-btn"
                    className="btn btn-primary px-5 py-2 text-[13px] disabled:opacity-40"
                  >
                    Next <ChevronRight size={13} />
                  </button>
                ) : (
                  <button
                    onClick={doPublish}
                    disabled={publishing}
                    data-testid="publish-confirm-btn"
                    className="btn btn-primary px-5 py-2 text-[13px] disabled:opacity-40"
                    style={{ boxShadow: `0 0 22px -8px ${meta.glow}` }}
                  >
                    {publishing ? <>Publishing… <Loader2 size={13} className="animate-spin" /></> : <>Publish <Share2 size={13} /></>}
                  </button>
                )}
              </>
            )}
            {step === 3 && (
              <button
                onClick={closeAndReset}
                data-testid="publish-done-btn"
                className="btn btn-primary w-full py-2.5 text-[13px]"
              >
                Done <Sparkles size={13} />
              </button>
            )}
          </footer>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function PriceSlider({ value, onChange, max, color }) {
  return (
    <div data-testid="publish-price-slider">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5 text-[13px] text-[#B0AEBC]">
          <Gem size={13} style={{ color }} fill={color} /> Per-import price
        </div>
        <div className="text-[28px] font-bold tabular-nums" style={{ color, textShadow: `0 0 16px ${color}66` }}>
          {value === 0 ? "FREE" : `${value} gem${value > 1 ? "s" : ""}`}
        </div>
      </div>

      {/* Slider buttons — discrete steps from 0..max */}
      <div className="flex items-center gap-1.5 mt-2">
        {Array.from({ length: max + 1 }, (_, i) => (
          <button
            key={i}
            onClick={() => onChange(i)}
            data-testid={`publish-price-${i}`}
            className="flex-1 h-9 rounded-md text-[12px] font-bold tabular-nums transition-all"
            style={{
              background: i === value
                ? `linear-gradient(135deg, ${color}, #7C5CFF)`
                : i <= value
                ? `${color}33`
                : "rgba(255,255,255,0.04)",
              color: i <= value ? "white" : "#71717A",
              boxShadow: i === value ? `0 0 16px -2px ${color}aa` : "none",
              border: `1px solid ${i === value ? color : "rgba(255,255,255,0.06)"}`,
            }}
          >
            {i}
          </button>
        ))}
      </div>
      <div className="flex justify-between text-[10.5px] text-[#71717A] mt-2">
        <span>Free</span>
        <span>Max {max}g for this rarity</span>
      </div>
    </div>
  );
}

function Hint({ title, body }) {
  return (
    <div className="bg-white/[0.025] rounded-xl p-3 border border-white/[0.05]">
      <div className="text-[12.5px] font-semibold text-white mb-1">{title}</div>
      <div className="text-[11.5px] text-[#8E8B99] leading-snug">{body}</div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-start gap-3 text-[13px]">
      <div className="w-24 text-[#71717A] uppercase tracking-[0.12em] text-[10.5px] font-bold pt-0.5">{k}</div>
      <div className="flex-1 text-[#E5E5E7] break-words">{v}</div>
    </div>
  );
}
